import { useRef, useState } from 'react'
import { AlertTriangle, Info, Loader2, CheckCircle2 } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  cancelHotelBooking,
  cancelOrder,
  requestFlightCancellationQuote,
  confirmFlightCancellation,
  type CancellationResult,
  type FlightCancellationQuote,
} from '@/lib/bookings-api'
import { formatCurrency } from '@/lib/utils'

interface CancellationModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  orderId: string
  itemType: 'hotel' | 'flight' | 'order'
  ratehawkOrderId?: string
  /** Set when the flight is booked on Duffel; selects the 2-step flight cancellation flow. */
  duffelOrderId?: string
  /** order_items row ID. The backend cancels the booking of this item (VOY-567). */
  orderItemId?: string
  totalAmount?: number
  cancellationPolicy?: string
}

type Step = 'initial' | 'quoting' | 'quote' | 'confirming' | 'done'

export function CancellationModal({
  isOpen,
  onClose,
  onSuccess,
  orderId,
  itemType,
  ratehawkOrderId,
  duffelOrderId,
  orderItemId,
  totalAmount,
  cancellationPolicy
}: CancellationModalProps) {
  const [step, setStep] = useState<Step>('initial')
  const [error, setError] = useState<string | null>(null)
  const [quote, setQuote] = useState<FlightCancellationQuote | null>(null)
  const [warning, setWarning] = useState<string | null>(null)
  const [refundPending, setRefundPending] = useState(false)
  // Ref-based guard against double-submit: setState is batched so
  // isProcessing can read stale between two clicks in the same
  // microtask. The ref flips synchronously before the await.
  const busyRef = useRef(false)

  const resetState = () => {
    setStep('initial')
    setError(null)
    setQuote(null)
    setWarning(null)
    setRefundPending(false)
    busyRef.current = false
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  // ── Flight 2-step flow ──────────────────────────────────────────

  const handleFlightCancelRequest = async () => {
    if (busyRef.current) return
    if (!orderItemId) {
      setError('Flight booking ID is missing. Please contact support.')
      return
    }
    busyRef.current = true
    setStep('quoting')
    setError(null)
    try {
      const q = await requestFlightCancellationQuote(orderItemId)
      setQuote(q)
      setStep('quote')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get cancellation quote')
      setStep('initial')
    } finally {
      busyRef.current = false
    }
  }

  const handleFlightCancelConfirm = async () => {
    if (!quote || !orderItemId || busyRef.current) return
    busyRef.current = true
    setStep('confirming')
    setError(null)
    try {
      // result.warning lives at the response root (not inside data) —
      // verified against backend flightsRoutes.js:528 which spreads it
      // at the same level as success and data.
      const result = await confirmFlightCancellation(orderItemId)
      if (result.warning) setWarning(result.warning)
      setRefundPending(result.refundStatus === 'pending')
      setStep('done')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to confirm cancellation')
      setStep('quote') // Let them retry confirm
    } finally {
      busyRef.current = false
    }
  }

  // ── Hotel / order legacy flow ───────────────────────────────────

  const handleLegacyCancel = async () => {
    setStep('confirming')
    setError(null)
    try {
      let result: CancellationResult
      if (itemType === 'order') {
        result = await cancelOrder(orderId)
      } else if (itemType === 'hotel' && ratehawkOrderId) {
        result = await cancelHotelBooking(ratehawkOrderId)
      } else {
        result = await cancelOrder(orderId)
      }
      if (result.success) {
        setStep('done')
      } else {
        setError(result.error || result.details || 'Failed to cancel booking')
        setStep('initial')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred')
      setStep('initial')
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────

  const isFlightWithDuffel = itemType === 'flight' && !!duffelOrderId
  const isProcessing = step === 'quoting' || step === 'confirming'

  const refundAmount = quote ? parseFloat(quote.refundAmount) : 0
  const isNonRefundable = quote && refundAmount === 0
  // VOY-616: amounts come from the backend quote: what was charged, what goes back to the card
  // and what does not. totalAmount (from the trip page) is only the fallback for the first line.
  const chargedAmount = quote?.chargedAmount != null ? parseFloat(quote.chargedAmount) : totalAmount
  const nonRefundableAmount = quote?.nonRefundableAmount != null
    ? parseFloat(quote.nonRefundableAmount)
    : (chargedAmount != null && quote ? chargedAmount - refundAmount : undefined)

  const getCancellationWarning = () => {
    if (cancellationPolicy) return cancellationPolicy
    if (itemType === 'hotel') {
      return 'Cancellation policies vary by hotel. Refunds, if applicable, typically process within 5-10 business days.'
    }
    return 'Cancellation policies vary by provider. Refunds, if applicable, typically process within 5-10 business days.'
  }

  // ── Render ──────────────────────────────────────────────────────

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === 'done' ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                Booking Cancelled
              </>
            ) : (
              <>
                <AlertTriangle className="h-5 w-5 text-orange-600" />
                Cancel Booking
              </>
            )}
          </DialogTitle>
          {step !== 'done' && (
            <DialogDescription>
              {step === 'quote'
                ? 'Review the refund details below before confirming.'
                : 'Are you sure you want to cancel this booking?'}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4">
          {/* ── Done state ── */}
          {step === 'done' && (
            <>
              <p className="text-sm text-muted-foreground">
                Your booking has been cancelled.
                {!refundPending && quote && refundAmount > 0 && (
                  <> A refund of <span className="font-semibold">{formatCurrency(refundAmount, quote?.refundCurrency)}</span> has been sent to your original payment method. It can take 5-10 business days to show on your statement.</>
                )}
                {refundPending && (
                  <> We could not send your refund{quote && refundAmount > 0 ? <> of <span className="font-semibold">{formatCurrency(refundAmount, quote.refundCurrency)}</span></> : null} automatically. Our team has been notified and will complete it; you do not need to do anything.</>
                )}
              </p>
              {warning && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-sm">{warning}</AlertDescription>
                </Alert>
              )}
              <div className="flex justify-end pt-2">
                <Button onClick={() => { handleClose(); onSuccess(); }}>
                  Done
                </Button>
              </div>
            </>
          )}

          {/* ── Quote step (flights only) ── */}
          {step === 'quote' && quote && (
            <>
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                {chargedAmount != null && (
                  <div className="flex justify-between text-sm">
                    <span>You paid</span>
                    <span>{formatCurrency(chargedAmount, quote?.refundCurrency)}</span>
                  </div>
                )}
                {nonRefundableAmount != null && nonRefundableAmount > 0 && (
                  <div className="flex justify-between text-sm text-orange-600">
                    <span>Not refundable</span>
                    <span>-{formatCurrency(nonRefundableAmount, quote?.refundCurrency)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold border-t pt-2">
                  <span>Refund to your card</span>
                  <span className={isNonRefundable ? 'text-red-600' : 'text-green-600'}>
                    {isNonRefundable ? 'Non-refundable' : formatCurrency(refundAmount, quote?.refundCurrency)}
                  </span>
                </div>
              </div>

              {nonRefundableAmount != null && nonRefundableAmount > 0 && !isNonRefundable && (
                <p className="text-xs text-muted-foreground">
                  The non-refundable part is the airline's cancellation penalty and Voy's service fee.
                </p>
              )}

              {quote.expiresAt && (
                <p className="text-xs text-muted-foreground">
                  This quote expires at {new Date(quote.expiresAt).toLocaleString()}.
                </p>
              )}

              {isNonRefundable && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    This booking is non-refundable. You will not receive any money back.
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}

          {/* ── Initial / quoting states ── */}
          {(step === 'initial' || step === 'quoting') && (
            <>
              {totalAmount != null && (
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Booking Amount:</span>
                    <span className="text-lg font-semibold">{formatCurrency(totalAmount)}</span>
                  </div>
                </div>
              )}
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  {getCancellationWarning()}
                </AlertDescription>
              </Alert>
            </>
          )}

          {/* ── Error ── */}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* ── Buttons ── */}
          {step !== 'done' && (
            <div className="flex gap-3 justify-end pt-4">
              <Button variant="outline" onClick={handleClose} disabled={isProcessing}>
                {step === 'quote' ? 'Keep Booking' : 'Cancel'}
              </Button>

              {/* Flight 2-step: initial → get quote */}
              {isFlightWithDuffel && step === 'initial' && (
                <Button variant="destructive" onClick={handleFlightCancelRequest}>
                  Get Refund Quote
                </Button>
              )}

              {/* Flight 2-step: quote → confirm */}
              {isFlightWithDuffel && step === 'quote' && (
                <Button variant="destructive" onClick={handleFlightCancelConfirm}>
                  {isNonRefundable ? 'Cancel Anyway' : 'Confirm Cancellation'}
                </Button>
              )}

              {/* Flight: loading states */}
              {isFlightWithDuffel && isProcessing && (
                <Button variant="destructive" disabled>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {step === 'quoting' ? 'Getting quote...' : 'Cancelling...'}
                </Button>
              )}

              {/* Hotel / order: immediate cancel */}
              {!isFlightWithDuffel && (
                <Button
                  variant="destructive"
                  onClick={handleLegacyCancel}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Cancelling...
                    </>
                  ) : (
                    'Confirm Cancellation'
                  )}
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
